import AlbumsByDownloadCodeMix from "../mixins/albums/GetAlbumsByDownloadCode";
import AlbumsByTagMix from "../mixins/albums/GetAlbumsByTag";
import UnlockMediaRequest, { UnlockTargetEnum } from "@/models/request/UnlockMediaRequest";
import { NormalizeParkRedemption } from "@helpers/albumContractAdapter";
import { GetApiErrorMessage } from "@helpers/apiErrorHelper";

export default {
    mixins: [AlbumsByDownloadCodeMix, AlbumsByTagMix],
    data: () => ({
        modalUnlockPark: false,
        modalDeleteTags: false,
        deleteTags: {
            tags: [],
            parkId: "",
            visitDate: "",
            uuid: "",
        },
        unlockPark: {
            idx: null,
            code: null,
            parkId: null,
            used: 0,
            limit: 0,
            remaining: 0,
            albumKey: null,
        },
        albumComponent: "",
    }),
    computed: {},
    methods: {
        /**
         * It checks if the album is already in the shopping cart.
         * @param code - The code of the album to be checked.
         * @returns A boolean value.
         */
        ExistAlbumInShoppingCart(code) {
            // El code puede contener Codigo de descarga | Room | Tag
            code = code.toString();
            // Verificamos si existe un codigo en el carrito
            const hasSome = Object.keys(this.$store.state.shoppingCart).some(
                (item) => item === code
            );

            return hasSome;
        },
        /**
         * It updates the album in the shopping cart.
         * @param code - The code of the album to be updated
         */
        async UpdateAlbum(code) {
            try {
                this.$store.dispatch("setLoading", true);

                const parameters = {
                    type: 0,
                    params: { JoinedCode: code },
                };
                /* Calling the API to get the albums by email. */
                const response = await this.$store.dispatch(
                    "getAlbums",
                    parameters
                );

                if (this.ExistAlbumInShoppingCart(code)) {
                    this.$store.dispatch("removeItemShoppingCart", code);
                }

                response.forEach((album) => {
                    let albumData = this.albumsByDownloadCode(album);
                    this.UpdateAlbumInStore(albumData);
                });
                this.$store.dispatch("updateAlbumComponent", false);

                Vue.$toast.open({
                    message: this.$t("albums.album_update"),
                    type: "success",
                });
            } catch (error) {
                this.$store.dispatch("setLoading", false);

                Vue.$toast.open({
                    message: this.$t("albums.error_album_update"),
                    type: "error",
                });
            } finally {
                this.$store.dispatch("setLoading", false);
            }
        },
        /**
         * Replaces the matching download-code album in Vuex.
         *
         * @param {Object} albumData Updated album data.
         * @returns {Promise<void>} Resolves after the album is replaced.
         */
        async UpdateAlbumInStore(albumData) {
            this.$store.state.albumes.forEach((album, idx) => {
                if (album.code === albumData.code) {
                    Vue.set(this.$store.state.albumes, idx, albumData);
                }
            });
        },
        /**
         * Unlocks the selected park through the unified media endpoint.
         *
         * The album index remains local to Vuex and is not included in the AWS request.
         *
         * @returns {Promise<void>}
         */
        async AddVisitPark() {
            try {
                this.$store.dispatch("setLoading", true);
                const request = new UnlockMediaRequest({
                    unlockTarget: UnlockTargetEnum.PARK,
                    albumKey: this.unlockPark.albumKey,
                });
                await this.$store.dispatch("unlockMedia", {
                    idx: Number(this.unlockPark.idx),
                    ...request,
                });

                Vue.$toast.open({
                    message: this.$t("albums.park_is_unlocked"),
                    type: "success",
                });

                this.modalUnlockPark = false;
                this.$store.dispatch("setLoading", false);
            } catch (error) {
                this.$store.dispatch("setLoading", false);
                this.modalUnlockPark = false;

                Vue.$toast.open({
                    message: GetApiErrorMessage(
                        error,
                        this.$t("albums.error_park_is_unlocked")
                    ),
                    type: "error",
                });
            }
        },
        /**
         * Returns the canonical park-redemption state for one download-code album.
         *
         * @param {number} idx Album index.
         * @returns {{album:Object|null,parkRedemption:Object|null}} Album and redemption state.
         */
        GetParkUnlockState(idx) {
            const album = this.$store.state.albumes[idx] || null;
            const parkRedemption = NormalizeParkRedemption(
                album?.parkRedemption
            );

            return { album, parkRedemption };
        },
        /**
         * Opens the park-unlock confirmation for the selected experience.
         *
         * @param {number} idx Album index.
         * @param {number} parkId Park identifier.
         * @returns {void}
         */
        UnlockParks(idx, parkId) {
            const { album, parkRedemption } = this.GetParkUnlockState(idx);

            if (!album || !parkRedemption || parkRedemption.Remaining <= 0) {
                return;
            }

            const albumKey = (Array.isArray(album.park)
                ? album.park
                : []
            ).find(
                (item) => Number(item?.Park?.Id) === Number(parkId)
            )?.AlbumKey || null;

            if (!albumKey) {
                return;
            }

            this.unlockPark.idx = idx;
            this.unlockPark.used = parkRedemption.Used;
            this.unlockPark.limit = parkRedemption.Limit;
            this.unlockPark.remaining = parkRedemption.Remaining;
            this.unlockPark.code = album.code;
            this.unlockPark.parkId = Number(parkId);
            this.unlockPark.albumKey = albumKey;

            this.modalUnlockPark = true;
        },
        /**
         * Checks whether the package has already used every park unlock.
         *
         * @param {number} idx Album index.
         * @returns {boolean} True when remaining albums must be sold normally.
         */
        IsParkForSale(idx) {
            const { parkRedemption } = this.GetParkUnlockState(idx);

            return Boolean(
                parkRedemption &&
                parkRedemption.Limit > 0 &&
                parkRedemption.Remaining <= 0
            );
        },
        /**
         * Opens the tag-removal modal for an album.
         *
         * @param {Object} item Tag-search album.
         * @returns {void}
         */
        DeleteTagsAlbum(item) {
            this.modalDeleteTags = true;
            this.deleteTags.tags = item.Tags;
            this.deleteTags.parkId = item.ParkId;
            this.deleteTags.visitDate = item.VisitDate;
            this.deleteTags.uuid = item.uuid;
            this.deleteTags.item = item;
        },
        /**
         * Removes one tag and refreshes the matching dynamic album.
         *
         * @param {number} idx Tag index.
         * @returns {Promise<void>} Resolves after the album refresh.
         */
        async RemoveTagFromAlbum(idx) {
            try {
                this.deleteTags.tags.splice(idx, 1);
                let tags = this.deleteTags.tags;

                await this.GetAlbumByTag(
                    tags,
                    this.deleteTags.parkId,
                    this.deleteTags.visitDate,
                    true,
                    this.deleteTags.item?.eventDetails || {}
                );

                Vue.$toast.open({
                    message: this.$t("messages.tag_remove_successfully"),
                    type: "success",
                });
            } catch (error) {
                Vue.$toast.open({
                    message: this.$t("messages.tag_error_remove"),
                    type: "error",
                });
            }
        },
        /**
         * Function to calculate the total media for a park.
         * @param {Object} park - The park object containing media details.
         * @return {string} - Returns a string with the total media count or selected media count if applicable.
         */
        CalculateTotalMedia(park) {
            if (
                park?.AlbumMode === 1 &&
                park?.DetailsServiceMedia?.Selected > 0
            ) {
                return (
                    park?.DetailsServiceMedia?.Selected + "/" + park?.TotalMedia
                );
            }
            return park?.TotalMedia;
        },
    },
};
